<template>
    <div class="mb-10 draggable bg-white rounded">
        <!-- Task Header. -->
        <div class="flex items-center mb-1">
            <div class="w-6 flex-shrink-0">
                <i class="fa fa-bars text-sm text-gray-400 hover:text-gray-500 cursor-move handle"></i>
            </div>
            <div class="w-12">
                <input
                    :placeholder="task.ref"
                    class="text-sm m-0 px-2 py-1 font-bold text-gray-900 border-transparent rounded shadow-none relative focus:z-10"
                    type="text"
                    v-model="reference"
                    @input="estimate.cannot_be_edited_by_authed_user ? null : $store.save()"
                    :disabled="estimate.cannot_be_edited_by_authed_user"
                >
            </div>
            <div class="flex-1 ml-4">
                <input
                    placeholder="Untitled task..."
                    class="text-sm m-0 px-2 py-1 font-bold text-gray-900 border-transparent rounded shadow-none relative focus:z-10"
                    type="text"
                    ref="name"
                    v-model="task.name"
                    @input="estimate.cannot_be_edited_by_authed_user ? null : $store.save()"
                    :disabled="estimate.cannot_be_edited_by_authed_user"
                >
            </div>
            <div class="w-6 flex-shrink-0 ml-2">
                <ui-dropdown menu-class="p-3 w-48">
                    <template #default="{ toggle }">
                        <button @click="toggle" class="text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative p-1 rounded">
                            <i class="fa fa-ellipsis-h"></i>
                        </button>
                    </template>

                    <template #menu="{ close }">
                        <ui-dropdown-item icon="fas fa-clipboard-list" @click="$modals.open('estimates-old-page-material-list-sidepage', { context: task, contextType: 'task' }); close();">See material list</ui-dropdown-item>
                        <ui-dropdown-item v-if="! estimate.cannot_be_edited_by_authed_user" icon="fas fa-copy" @click="$store.duplicateTask(section, task); close();">Duplicate</ui-dropdown-item>
                        <ui-dropdown-item v-if="! estimate.cannot_be_edited_by_authed_user" icon="fas fa-trash" danger @click="deleteTask">Delete</ui-dropdown-item>
                    </template>
                </ui-dropdown>
            </div>
        </div>

        <!-- Task Items. -->
        <draggable :list="task.items" draggable=".draggable" handle=".handle" group="items" @change="itemsReordered" :disabled="estimate.cannot_be_edited_by_authed_user">
            <estimates-old-page-task-item v-for="item in task.items" :key="item.key" :item="item" :task="task" ref="items"></estimates-old-page-task-item>
        </draggable>

        <!-- New Task Item. -->
        <div class="flex border-t border-b -mb-px border-gray-200" v-if="! estimate.cannot_be_edited_by_authed_user">
            <div class="w-6 flex-shrink-0 self-center">
                <i class="fa fa-plus text-xs text-gray-300"></i>
            </div>
            <div class="w-16">
                <input
                    :placeholder="nextItemReference"
                    class="text-sm -my-px px-2 py-1.5 border border-gray-200 shadow-none relative focus:z-10"
                    type="text"
                    v-model="newItem.reference"
                >
            </div>
            <div class="flex-1 -mr-px">
                <input
                    placeholder="Add a new item..."
                    class="text-sm -ml-px -my-px px-2 py-1.5 border border-gray-200 shadow-none relative focus:z-10"
                    type="text"
                    v-model="newItem.description"
                    @input="addItem"
                >
            </div>
            <div class="w-6 ml-2"></div>
        </div>

        <!-- Total Amount. -->
        <div class="flex text-xs text-gray-500 text-right font-semibold">
            <div class="flex-1 px-2 py-1">Total</div>
            <div class="w-28 -ml-px px-2 py-1">
                <ui-dropdown placement="bottom-start" menu-class="p-3 w-52 flex" class="flex w-full" trigger-class="flex w-full">
                    <template #default="{ open, close }">
                        <div @mouseenter="open" @mouseleave="close" class="w-full">
                            {{ task.cf_rate | toCurrency }}
                        </div>
                    </template>
                    <template #menu>
                        <table class="w-full">
                            <tbody>
                                <tr>
                                    <td class="text-gray-600 text-sm">Labour rates</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ specsDetails.rates.labour_rates | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Materials</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ specsDetails.rates.materials | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Other</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ specsDetails.rates.other | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Total cost</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ task.rate | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td colspan="2">
                                        <div class="border-b border-gray-200 my-1"></div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Markup</td>
                                    <td
                                        class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold"
                                        :class="hasNonDefaultMarkup ? 'text-pink-600' : ''"
                                    >
                                        {{ task.markup_rate | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">%</td>
                                    <td
                                        class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold"
                                        :class="hasNonDefaultMarkup ? 'text-pink-600' : ''"
                                    >
                                        {{ task.markup_pc }}%
                                    </td>
                                </tr>
                                <tr>
                                    <td colspan="2">
                                        <div class="border-b border-gray-200 my-1"></div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Total</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ task.cf_rate | toCurrency }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </template>
                </ui-dropdown>
            </div>
            <div class="w-28 -ml-px px-2 py-1">
                <ui-dropdown placement="bottom-start" menu-class="p-3 w-52 flex" class="flex w-full" trigger-class="flex w-full">
                    <template #default="{ open, close }">
                        <div @mouseenter="open" @mouseleave="close" class="w-full">
                            {{ task.cf_amount | toCurrency }}
                        </div>
                    </template>
                    <template #menu>
                        <table class="w-full">
                            <tbody>
                                <tr>
                                    <td class="text-gray-600 text-sm">Labour rates</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ specsDetails.amounts.labour_rates | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Materials</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ specsDetails.amounts.materials | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Other</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ specsDetails.amounts.other | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Total cost</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ task.amount | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td colspan="2">
                                        <div class="border-b border-gray-200 my-1"></div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Markup</td>
                                    <td
                                        class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold"
                                        :class="hasNonDefaultMarkup ? 'text-pink-600' : ''"
                                    >
                                        {{ task.markup_amount | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">%</td>
                                    <td
                                        class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold"
                                        :class="hasNonDefaultMarkup ? 'text-pink-600' : ''"
                                    >
                                        {{ task.markup_pc }}%
                                    </td>
                                </tr>
                                <tr>
                                    <td colspan="2">
                                        <div class="border-b border-gray-200 my-1"></div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Total</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ task.cf_amount | toCurrency }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </template>
                </ui-dropdown>
            </div>
            <div class="w-8 -ml-px px-2 py-1"></div>
            <div class="w-20 px-2 py-1"></div>
            <div class="w-6 flex-shrink-0 ml-2"></div>
        </div>
    </div>
</template>

<script>
import { estimateStore } from '@stores'
import draggable from 'vuedraggable'

export default {
    components: {
        draggable,
    },
    props: {
        section: Object,
        task: Object,
    },
    data () {
        return {
            newItem: {
                reference: null,
                description: null,
            },
            forceCustomRef: false,
        }
    },
    computed: {
        $store () {
            return estimateStore
        },
        estimate () {
            return this.$store.state.estimate
        },
        reference: {
            get () {
                return this.forceCustomRef || this.task.custom_ref
                    ? this.task.custom_ref
                    : this.task.ref
            },
            set (newValue) {
                if (newValue == this.task.ref) {
                    this.forceCustomRef = false
                    this.task.custom_ref = ''
                } else {
                    this.forceCustomRef = true
                    this.task.custom_ref = newValue
                }
            },
        },
        nextItemReference () {
            const itemNumber = this.task.items.length + 1
            return `${this.task.ref}.${itemNumber}`
        },
        hasNonDefaultMarkup () {
            return this.task.markup_pc !== this.estimate.markup_pc
        },
        specsDetails () {
            return this.$store.getTaskGroupDetails(this.task)
        },
    },
    methods: {
        itemsReordered () {
            this.$store.repopulateNumbers()
            this.$store.save()
        },
        deleteTask () {
            this.$modals.confirmDelete({
                title: `Delete task ${this.task.ref}`,
                button: 'Yes, delete task',
                handle: () => this.$store.deleteTask(this.section, this.task)
            })
        },
        addItem () {
            if (! this.newItem.description) return
            this.$store.addItem(this.task, {
                custom_ref: this.newItem.reference,
                description: this.newItem.description,
            })

            this.$nextTick(() => {
                const inputField = this.$refs.items[this.$refs.items.length - 1].$refs.description

                // This ensures the cursor position is at
                // the end of the field on certain browsers.
                inputField.focus();
                var val = inputField.value;
                inputField.value = '';
                inputField.value = val;

                this.newItem.reference = ''
                this.newItem.description = ''
            })
        }
    },
}
</script>
